'use client';

import { useCurrentTime } from '@shared/hooks';
import { useTranslations } from 'next-intl';
import { useEffect, useMemo, useState } from 'react';
import type { DrawingLotteryType } from '../../../lotteries/utils';
import type { Drawing, DrawingsResponse } from '../../types';
import { getDefaultDrawing, getPickerDrawings, type PickerDrawingOptions } from '../../utils';

const ONE_SECOND = 1000;
const ONE_MINUTE = 60 * ONE_SECOND;

type DrawingDateOption = { value: string; text: string };

type UseCurrentResultOptions = Pick<PickerDrawingOptions, 'lotteryName' | 'nextDrawingDate' | 'nextClosingDate' | 'nextDrawingId' | 'nextCloseLeadTime' | 'maxDrawings'>;

function mapDrawingDates(data: Drawing[], formatDate: (d: Date) => string): DrawingDateOption[] {
  return data.map(drawing => ({
    value: drawing.drawingDate,
    text: formatDate(new Date(drawing.drawingDate)),
  }));
}

/**
 * Hook to get the current result and the drawing dates. This hook sorts draws newest-first,
 * maps them to date picker options, and selects a default draw. When a next-close lead time is
 * provided, the picker includes the upcoming draw once that much time remains until close
 * and keeps its numbers withheld until that drawing time. It follows {@link useCurrentTime} at
 * one-minute resolution, the same clock as the results header, so a stepped clock releases the
 * numbers without a timeout of its own.
 *
 * @param data - The drawings response.
 * @param drawFrequency - The lottery draw frequency used to format picker labels.
 * @param options - Optional next-draw data used to choose the default date.
 * @returns The current result and the drawing dates.
 */
function useCurrentResult(data: DrawingsResponse, drawFrequency: DrawingLotteryType = 'NORMAL', options: UseCurrentResultOptions = {}) {
  const t = useTranslations('lotteries.dates');
  const [currentResult, setCurrentResult] = useState<Drawing | null>(null);
  const { lotteryName, nextDrawingDate, nextClosingDate, nextDrawingId, nextCloseLeadTime, maxDrawings } = options;
  const now = useCurrentTime({ updatePeriod: ONE_SECOND, resolution: ONE_MINUTE });
  const pickerOptions: PickerDrawingOptions = useMemo(
    () => ({ lotteryName, nextDrawingDate, nextClosingDate, nextDrawingId, nextCloseLeadTime, maxDrawings, now }),
    [lotteryName, nextDrawingDate, nextClosingDate, nextDrawingId, nextCloseLeadTime, maxDrawings, now]
  );

  const pickerDrawings = useMemo(() => getPickerDrawings(data, pickerOptions), [data, pickerOptions]);

  const drawingDates = useMemo(() => {
    const dateKey = ['HIGH_FREQUENCY', 'GO'].includes(drawFrequency) ? 'results_dropdown_date_with_time' : 'results_dropdown_date';
    return mapDrawingDates(pickerDrawings, (date: Date) => t(dateKey, { date }));
  }, [pickerDrawings, t, drawFrequency]);

  useEffect(() => {
    if (pickerDrawings.length === 0) {
      // Calling setState synchronously within an effect body causes cascading renders that can hurt performance, and is not recommended. (https://react.dev/learn/you-might-not-need-an-effect).
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setCurrentResult(null);
      return;
    }

    setCurrentResult((previous: Drawing | null) => {
      if (!previous) return getDefaultDrawing(pickerDrawings, pickerOptions);

      return (
        pickerDrawings.find((drawing: Drawing) => {
          if (previous.drawingId && drawing.drawingId === previous.drawingId) return true;
          return drawing.drawingDate === previous.drawingDate;
        }) ?? previous
      );
    });
  }, [pickerDrawings, pickerOptions]);

  const handleResultChange = (drawingDate: string) => {
    const found = pickerDrawings.find((drawing: Drawing) => drawing.drawingDate === drawingDate);
    if (found) setCurrentResult(found);
  };

  return {
    currentResult,
    drawingDates,
    handleResultChange,
  };
}

export { useCurrentResult };
export type { UseCurrentResultOptions };
